<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('expenses.title') }}</h1>
          <div>
            <v-btn variant="outlined" class="mr-2" @click="openAddExpenseTypeDialog">{{ $t('expenses.addType') }}</v-btn>
            <v-btn color="primary" @click="openAddDialog">{{ $t('expenses.add') }}</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <v-card variant="tonal" class="pa-4">
          <v-card-title class="text-h6 mb-4">{{ $t('expenses.filters') }}</v-card-title>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filterWallet"
                v-model:search="walletFilterSearch"
                :items="filteredWalletsForFilter"
                item-title="display_name"
                item-value="id"
                :label="$t('expenses.wallet')"
                :placeholder="$t('expenses.walletSearchPlaceholder')"
                clearable
                density="compact"
                auto-select-first
                @update:model-value="(val) => {
                  if (!val) {
                    walletFilterSearch = ''
                  }
                }"
              >
                <template #no-data>
                  <div class="pa-2">{{ $t('expenses.noWalletsFound') }}</div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterExpensesType"
                :items="expensesTypes"
                item-title="name"
                item-value="id"
                :label="$t('expenses.expenseType')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('expenses.dateFrom')"
                type="date"
                density="compact"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filterDateTo"
                :label="$t('expenses.dateTo')"
                type="date"
                density="compact"
                clearable
              />
            </v-col>
          </v-row>
          <v-row class="period-buttons-row">
            <v-col cols="12" sm="6" md="6"></v-col>
            <v-col cols="12" sm="6" md="4">
              <v-btn-toggle v-model="datePeriod" mandatory class="period-buttons">
                <v-btn value="day" size="small">{{ $t('expenses.day') }}</v-btn>
                <v-btn value="week" size="small">{{ $t('expenses.week') }}</v-btn>
                <v-btn value="month" size="small">{{ $t('expenses.month') }}</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('expenses.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th 
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('transaction_date')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('expenses.dateColumn') }}
                      <v-icon size="small" class="ml-1">{{ getSortIcon('transaction_date') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('wallet')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('expenses.walletColumn') }}
                      <v-icon size="small" class="ml-1">{{ getSortIcon('wallet') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('type')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('expenses.typeColumn') }}
                      <v-icon size="small" class="ml-1">{{ getSortIcon('type') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('amount')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('expenses.amountColumn') }}
                      <v-icon size="small" class="ml-1">{{ getSortIcon('amount') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('amount_usdt')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('expenses.amountUsdtColumn') }}
                      <v-icon size="small" class="ml-1">{{ getSortIcon('amount_usdt') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('user')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('expenses.userColumn') }}
                      <v-icon size="small" class="ml-1">{{ getSortIcon('user') }}</v-icon>
                    </div>
                  </th>
                  <th>{{ $t('expenses.descriptionColumn') }}</th>
                  <th>{{ $t('expenses.actionsColumn') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="expense in filteredExpenses" :key="expense.id">
                  <td>{{ new Date(expense.transaction_date).toLocaleDateString(locale.value === 'ru' ? 'ru-RU' : 'en-US') }}</td>
                  <td>{{ expense.wallet_display }}</td>
                  <td>{{ expense.expenses_type_name }}</td>
                  <td class="text-right">{{ expense.amount }}</td>
                  <td class="text-right">{{ expense.amount_usdt }}</td>
                  <td>{{ expense.user_email || '-' }}</td>
                  <td>{{ expense.description || '-' }}</td>
                  <td>
                    <v-btn variant="plain" size="small" :title="$t('walletOperations.linkTitle')" icon="mdi-format-list-bulleted" @click="$router.push({ path: '/wallet-operations', query: { document_type: 'Expense', document_id: expense.id } })"></v-btn>
                    <v-btn variant="plain" size="small" @click="editExpense(expense)" icon="mdi-pencil"></v-btn>
                    <v-btn variant="plain" size="small" color="error" @click="deleteExpense(expense.id)" icon="mdi-delete"></v-btn>
                  </td>
                </tr>
                <tr v-if="filteredExpenses.length === 0">
                  <td colspan="8" class="text-center text-grey">{{ $t('expenses.noExpensesFound') }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="4" class="text-right font-weight-bold">{{ $t('expenses.total') }}</td>
                  <td class="text-right font-weight-bold">{{ formattedTotalExpenses }}</td>
                  <td colspan="3"></td>
                </tr>
              </tfoot>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <ExpenseEditDialog
      v-model="showAddDialog"
      :expense="editingExpense"
      :wallets="wallets"
      :expenses-types="expensesTypes"
      :users="users"
      @save="handleSaveExpense"
      @cancel="handleCancelExpense"
    />

    <v-dialog v-model="showAddExpenseTypeDialog" max-width="400" @update:model-value="(val) => { if (!val) resetExpenseTypeForm(); }">
      <v-card>
        <v-card-title>{{ $t('expenses.addExpenseType') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="expenseTypeForm.name"
              :label="$t('expenses.name')"
              :placeholder="$t('expenses.namePlaceholder')"
              required
              :rules="[v => !!v || $t('expenses.nameRequired')]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddExpenseTypeDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveExpenseType">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('common.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../utils/api'
import { getDateRangeForPeriod, getStartOfDay, getEndOfDay, formatDateForDisplay } from '../utils/date'
import ExpenseEditDialog from './dialogs/ExpenseEditDialog.vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

interface Wallet {
  id: number
  display_name: string
  usdt_rate: number
  exchange?: {
    id: number
    name: string
  } | null
  coin: {
    id: number
    name: string
    symbol: string
  }
}

interface ExpensesType {
  id: number
  name: string
}

interface Expense {
  id: number
  wallet_id: number
  expenses_type_id: number
  user_id: number
  amount: number
  amount_usdt: number
  description: string | null
  transaction_date: string
  wallet_display: string
  expenses_type_name: string
  user_email?: string
}

interface User {
  id: number
  email: string
}

const expenses = ref<Expense[]>([])
const wallets = ref<Wallet[]>([])
const expensesTypes = ref<ExpensesType[]>([])
const users = ref<User[]>([])
const showAddDialog = ref(false)
const editingExpense = ref<Expense | null>(null)
const showAddExpenseTypeDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const expenseTypeForm = ref({
  name: ''
})

const filterWallet = ref<number | null>(null)
const filterExpensesType = ref<number | null>(null)
const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)
const datePeriod = ref<string>('day')
const walletFilterSearch = ref('')

// Sort state - default to date ascending
const sortBy = ref<string>('transaction_date')
const sortOrder = ref<'asc' | 'desc'>('asc')

const expenseForm = ref({
  id: null as number | null,
  wallet_id: null as number | null,
  expenses_type_id: null as number | null,
  user_id: null as number | null,
  amount: 0,
  amount_usdt: 0,
  description: '',
  transaction_date: new Date().toISOString().split('T')[0]
})

const loadExpenses = async () => {
  try {
    const response = await apiGet('/api/expenses')
    if (response.ok) {
      expenses.value = await response.json()
    }
  } catch (error) {
    console.error('Load expenses error:', error)
  }
}

const loadWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const walletData = await response.json()
      wallets.value = walletData.map((w: any) => {
        const exchangeName = w.exchange?.name || ''
        const coinSymbol = w.coin?.symbol || ''
        const displayName = exchangeName ? `${exchangeName} - ${coinSymbol}` : coinSymbol
        return {
          ...w,
          display_name: displayName,
          usdt_rate: w.usdt_rate || 0
        }
      })
    }
  } catch (error) {
    console.error('Load wallets error:', error)
  }
}

const loadExpensesTypes = async () => {
  try {
    const response = await apiGet('/api/expenses-types')
    if (response.ok) {
      expensesTypes.value = await response.json()
    }
  } catch (error) {
    console.error('Load expenses types error:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

const loadCurrentUser = async () => {
  try {
    const response = await apiGet('/api/users/current')
    if (response.ok) {
      const userData = await response.json()
      return userData.id
    }
  } catch (error) {
    console.error('Load current user error:', error)
  }
  return null
}

const calculateAmountUsdt = () => {
  if (expenseForm.value.wallet_id && expenseForm.value.amount) {
    const wallet = wallets.value.find(w => w.id === expenseForm.value.wallet_id)
    if (wallet && wallet.usdt_rate) {
      const amount = parseFloat(expenseForm.value.amount?.toString() || '0')
      const rate = parseFloat(wallet.usdt_rate?.toString() || '0')
      expenseForm.value.amount_usdt = Math.round(amount * rate * 100000000) / 100000000
    }
  } else {
    expenseForm.value.amount_usdt = 0
  }
}

const filteredWalletsForFilter = computed(() => {
  if (!wallets.value || wallets.value.length === 0) return []
  if (!walletFilterSearch.value) return wallets.value
  const search = walletFilterSearch.value.toLowerCase()
  return wallets.value.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

// Set date period
const setDatePeriod = (period: string) => {
  const dateRange = getDateRangeForPeriod(period)
  filterDateFrom.value = dateRange.from
  filterDateTo.value = dateRange.to
}

// Watch date period changes
watch(datePeriod, (newPeriod) => {
  setDatePeriod(newPeriod)
})

// Calculate total expenses from filtered expenses
const totalExpenses = computed((): number => {
  return filteredExpenses.value.reduce((sum, expense) => {
    return sum + (parseFloat(expense.amount_usdt?.toString() || '0') || 0)
  }, 0)
})

const formattedTotalExpenses = computed(() => {
  return new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(totalExpenses.value)
})

// Sort handler
const handleSort = (column: string) => {
  if (sortBy.value === column) {
    // Toggle sort order if clicking the same column
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new column and default to ascending
    sortBy.value = column
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (column: string) => {
  if (sortBy.value !== column) return 'mdi-sort'
  return sortOrder.value === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'
}

// Format expense amount to 2 decimal places
const formatExpenseAmount = (amount: number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return '0.00'
  return value.toFixed(2)
}

const filteredExpenses = computed(() => {
  let filtered = expenses.value

  if (filterWallet.value !== null) {
    filtered = filtered.filter(e => e.wallet_id === filterWallet.value)
  }

  if (filterExpensesType.value !== null) {
    filtered = filtered.filter(e => e.expenses_type_id === filterExpensesType.value)
  }

  if (filterDateFrom.value) {
    const fromDate = getStartOfDay(filterDateFrom.value)
    filtered = filtered.filter(e => {
      const expenseDate = getStartOfDay(e.transaction_date)
      return expenseDate >= fromDate
    })
  }

  if (filterDateTo.value) {
    const toDate = getEndOfDay(filterDateTo.value)
    filtered = filtered.filter(e => {
      const expenseDate = new Date(e.transaction_date)
      return expenseDate <= toDate
    })
  }

  // Sort expenses
  const sorted = [...filtered].sort((a, b) => {
    let aValue: any
    let bValue: any

    switch (sortBy.value) {
      case 'transaction_date':
        aValue = new Date(a.transaction_date).getTime()
        bValue = new Date(b.transaction_date).getTime()
        break
      case 'wallet':
        aValue = a.wallet_display || ''
        bValue = b.wallet_display || ''
        break
      case 'type':
        aValue = a.expenses_type_name || ''
        bValue = b.expenses_type_name || ''
        break
      case 'amount':
        aValue = a.amount || 0
        bValue = b.amount || 0
        break
      case 'amount_usdt':
        aValue = a.amount_usdt || 0
        bValue = b.amount_usdt || 0
        break
      case 'user':
        aValue = a.user_email || ''
        bValue = b.user_email || ''
        break
      default:
        return 0
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder.value === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    } else {
      return sortOrder.value === 'asc'
        ? (aValue > bValue ? 1 : aValue < bValue ? -1 : 0)
        : (aValue < bValue ? 1 : aValue > bValue ? -1 : 0)
    }
  })

  return sorted
})

const openAddDialog = async () => {
  const userId = await loadCurrentUser()
  expenseForm.value.user_id = userId
  resetForm()
  showAddDialog.value = true
}

const editExpense = (expense: Expense) => {
  editingExpense.value = expense
  showAddDialog.value = true
}

const handleSaveExpense = async (formData: any) => {
  if (!formData.wallet_id || !formData.expenses_type_id || !formData.user_id || !formData.amount || !formData.amount_usdt || !formData.transaction_date) {
    showError(t('expenses.fillAllFields'))
    return
  }

  try {
    const expenseData = {
      wallet_id: formData.wallet_id,
      expenses_type_id: formData.expenses_type_id,
      user_id: formData.user_id,
      amount: formData.amount,
      amount_usdt: formData.amount_usdt,
      description: formData.description,
      transaction_date: formData.transaction_date
    }

    const url = formData.id ? `/api/expenses/${formData.id}` : '/api/expenses'
    const response = formData.id
      ? await apiPut(url, { expense: expenseData })
      : await apiPost(url, { expense: expenseData })

    if (response.ok) {
      await loadExpenses()
      showAddDialog.value = false
      editingExpense.value = null
    } else {
      const errorData = await response.json()
      const errorText = errorData.errors?.join(', ') || t('errors.unknownError')
      showError(t('expenses.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save expense error:', error)
    const errorText = error instanceof Error ? error.message : t('expenses.errorSaving')
    showError(`${t('expenses.errorSaving')}: ${errorText}`)
  }
}

const handleCancelExpense = () => {
  editingExpense.value = null
}

const resetForm = async () => {
  const userId = await loadCurrentUser()
  expenseForm.value = {
    id: null,
    wallet_id: null,
    expenses_type_id: null,
    user_id: userId,
    amount: 0,
    amount_usdt: 0,
    description: '',
    transaction_date: new Date().toISOString().split('T')[0]
  }
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}


const deleteExpense = async (id: number) => {
  if (!confirm(t('expenses.areYouSureDelete'))) return
  try {
    const response = await apiDelete(`/api/expenses/${id}`)
    if (response.ok) {
      await loadExpenses()
    }
  } catch (error) {
    console.error('Delete expense error:', error)
    showError(t('expenses.errorDeleting'))
  }
}

const openAddExpenseTypeDialog = () => {
  resetExpenseTypeForm()
  showAddExpenseTypeDialog.value = true
}

const resetExpenseTypeForm = () => {
  expenseTypeForm.value = {
    name: ''
  }
}

const saveExpenseType = async () => {
  if (!expenseTypeForm.value.name) {
    showError(t('expenses.fillInName'))
    return
  }

  try {
    const response = await apiPost('/api/expenses-types', { expenses_type: expenseTypeForm.value })

    if (response.ok) {
      await loadExpensesTypes()
      showAddExpenseTypeDialog.value = false
      resetExpenseTypeForm()
    } else {
      const errorText = await getErrorMessage(response, t('errors.unknownError'))
      showError(t('expenses.failedToSaveType', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save expense type error:', error)
    const errorText = error instanceof Error ? error.message : t('expenses.errorSavingType')
    showError(`${t('expenses.errorSavingType')}: ${errorText}`)
  }
}

onMounted(() => {
  loadExpenses()
  loadWallets()
  loadExpensesTypes()
  loadUsers()
  // Set default period to today
  setDatePeriod('day')
})
</script>

<style scoped>
.period-buttons-row {
  margin-top: -38px;
}

.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}

.total-row {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
